/**
 * The KnowIt adapter for the authenticated `profiles.*` family (W9 §A.4, §C.3).
 *
 * `profiles.getPublic`/`profiles.search` stay where they already are (`ApiClient.public.profile`,
 * `client.ts`'s `research.post`) — both are `[AllowAnonymous]` and already typed. This module
 * carries the OWNER'S half: `getOwn`, `update`, the skill/portfolio writes and `cv.export`,
 * none of which slate could reach before this wave except through the untyped
 * `api.research.post(...)` escape hatch (§A.4 P3).
 *
 * `profiles.update` answers **409 `{error:"That profile URL is already taken."}`** on a slug
 * collision — translated to `ConflictError` here so a form can show it inline, next to the
 * slug field, rather than as a generic toast.
 */
import { ApiError, ConflictError } from "~/api/errors";
import type {
  AnswerTelemetry,
  CvInterview,
  CvInterviewState,
  PortfolioEntry,
  ProfileCv,
  ProfilePatch,
  ProfileSearchPage,
  ProfileSearchQuery,
  SkillLevel,
} from "~/api/types";
import {
  CvInterviewSchema,
  CvInterviewStateSchema,
  ProfileCvSchema,
  ProfileSearchPageSchema,
} from "~/api/types";

type Json = Record<string, unknown>;

function obj(value: unknown): Json {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Json) : {};
}

export interface ProfilesTransport {
  post(path: string, body?: Record<string, unknown>): Promise<unknown>;
}

async function postConflictAware(
  transport: ProfilesTransport,
  path: string,
  body?: Record<string, unknown>,
): Promise<unknown> {
  try {
    return await transport.post(path, body);
  } catch (error) {
    if (error instanceof ApiError && error.status === 409) {
      throw new ConflictError(error.message || "That profile URL is already taken.");
    }
    throw error;
  }
}

export function createKnowItProfiles(transport: ProfilesTransport) {
  return {
    own: async (): Promise<ProfileCv> => {
      const raw = obj(await transport.post("profiles.getOwn", {}));
      return ProfileCvSchema.parse(obj(raw.data));
    },

    update: async (patch: ProfilePatch): Promise<ProfileCv> => {
      const raw = obj(await postConflictAware(transport, "profiles.update", { ...patch }));
      return ProfileCvSchema.parse(obj(raw.data));
    },

    upsertSkill: async (input: {
      name: string;
      level?: SkillLevel;
      projectsUsedCount?: number;
    }): Promise<ProfileCv> => {
      const raw = obj(
        await transport.post("profiles.skills.upsert", {
          name: input.name,
          ...(input.level ? { level: input.level } : {}),
          ...(input.projectsUsedCount !== undefined
            ? { projectsUsedCount: input.projectsUsedCount }
            : {}),
        }),
      );
      return ProfileCvSchema.parse(obj(raw.data));
    },

    removeSkill: async (skillId: string): Promise<ProfileCv> => {
      const raw = obj(await transport.post("profiles.skills.remove", { skillId }));
      return ProfileCvSchema.parse(obj(raw.data));
    },

    setPortfolio: async (entries: PortfolioEntry[]): Promise<ProfileCv> => {
      const raw = obj(await transport.post("profiles.portfolio.set", { entries }));
      return ProfileCvSchema.parse(obj(raw.data));
    },

    exportCv: async (): Promise<{ format: "json"; exportedAt: string; cv: ProfileCv }> => {
      const raw = obj(await transport.post("profiles.cv.export", {}));
      const data = obj(raw.data);
      return {
        format: "json",
        exportedAt: String(data.exportedAt ?? new Date(0).toISOString()),
        cv: ProfileCvSchema.parse(obj(data.cv)),
      };
    },

    /**
     * The system CV interview (`profiles.interview.*`). The server owns the script: every verb
     * answers the WHOLE session, so the scene renders what came back instead of tracking a stage
     * of its own. `answer` can take a while — a local model writes the next question, and the
     * last answer also triggers the scoring pass.
     */
    /**
     * The system CV interview (`profiles.interview.*`). The server owns the script: every verb
     * answers the WHOLE sitting, so the scene renders what came back instead of tracking a state
     * machine of its own. `answer` can take a while — a local model writes the next question, and
     * a section closing also scores it.
     */
    interview: {
      get: async (): Promise<CvInterviewState> => {
        const raw = obj(await transport.post("profiles.interview.get", {}));
        return CvInterviewStateSchema.parse(obj(raw.data));
      },

      start: async (): Promise<CvInterview> => {
        const raw = obj(await transport.post("profiles.interview.start", {}));
        return CvInterviewSchema.parse(obj(raw.data));
      },

      answer: async (input: {
        interviewId: string;
        text: string;
        telemetry?: AnswerTelemetry;
      }): Promise<CvInterview> => {
        const raw = obj(
          await transport.post("profiles.interview.answer", {
            interviewId: input.interviewId,
            text: input.text,
            // Absent telemetry is a real answer, not a failure: the server reads it as
            // "unverified", which is a statement about our evidence and not about the person.
            ...(input.telemetry ? { telemetry: input.telemetry } : {}),
          }),
        );
        return CvInterviewSchema.parse(obj(raw.data));
      },

      /** Correct an answer already given; the section it belongs to is re-read server-side. */
      amend: async (input: {
        interviewId: string;
        turnIndex: number;
        text: string;
      }): Promise<CvInterview> => {
        const raw = obj(
          await transport.post("profiles.interview.amend", {
            interviewId: input.interviewId,
            turnIndex: input.turnIndex,
            text: input.text,
          }),
        );
        return CvInterviewSchema.parse(obj(raw.data));
      },

      /** Correct a line of the CV itself; the correction survives every later refold. */
      editDraft: async (input: {
        interviewId: string;
        field: string;
        value: string | null;
      }): Promise<CvInterview> => {
        const raw = obj(
          await transport.post("profiles.interview.editDraft", {
            interviewId: input.interviewId,
            field: input.field,
            value: input.value,
          }),
        );
        return CvInterviewSchema.parse(obj(raw.data));
      },

      pause: async (interviewId: string): Promise<CvInterview> => {
        const raw = obj(await transport.post("profiles.interview.pause", { interviewId }));
        return CvInterviewSchema.parse(obj(raw.data));
      },

      resume: async (interviewId: string): Promise<CvInterview> => {
        const raw = obj(await transport.post("profiles.interview.resume", { interviewId }));
        return CvInterviewSchema.parse(obj(raw.data));
      },

      publish: async (input: {
        interviewId: string;
        publish: boolean;
        acceptSkills?: boolean;
        publishFacts?: boolean;
        publishRate?: boolean;
        applyToCv?: boolean;
      }): Promise<{ profile: ProfileCv; interview: CvInterview }> => {
        const raw = obj(
          await transport.post("profiles.interview.publish", {
            interviewId: input.interviewId,
            publish: input.publish,
            ...(input.acceptSkills === undefined ? {} : { acceptSkills: input.acceptSkills }),
            ...(input.publishFacts === undefined ? {} : { publishFacts: input.publishFacts }),
            ...(input.publishRate === undefined ? {} : { publishRate: input.publishRate }),
            ...(input.applyToCv === undefined ? {} : { applyToCv: input.applyToCv }),
          }),
        );
        const data = obj(raw.data);
        return {
          profile: ProfileCvSchema.parse(obj(data.profile)),
          interview: CvInterviewSchema.parse(obj(data.interview)),
        };
      },

      discard: async (interviewId: string): Promise<CvInterview> => {
        const raw = obj(await transport.post("profiles.interview.discard", { interviewId }));
        return CvInterviewSchema.parse(obj(raw.data));
      },
    },

    search: async (query: ProfileSearchQuery): Promise<ProfileSearchPage> => {
      const raw = obj(
        await transport.post("profiles.search", {
          ...(query.skills?.length ? { skills: query.skills } : {}),
          ...(query.availability ? { availability: query.availability } : {}),
          ...(query.timezone ? { timezone: query.timezone } : {}),
          offset: query.offset ?? 0,
          limit: query.limit ?? 25,
        }),
      );
      return ProfileSearchPageSchema.parse(raw);
    },
  };
}
